import { UseFormReturn } from 'react-hook-form';
import { SnackbarContextType } from '../contexes/SnackBarProvider';
import { AnalyticsEvents } from '../enums/AnalyticsEvents';
import {
  Agent,
  AgentFormData,
  Appointment,
  DepartmentContact,
} from '../interfaces/Agent';
import Company from '../interfaces/Company';
import Knowledge, { KnowledgeFileData } from '../interfaces/Knowledge';
import User from '../interfaces/User';
import { TranslationKey } from '../types/i18n';
import { updateAgent } from '../services/agentService';
import { deleteKnowledge, updateKnowledge } from '../services/knowledgeService';
import { toAccessPayload } from '../utils/resourceAccess';
import {
  getTransferCallsArray,
  removeWhiteSpaces,
  setAgentsImageFile,
} from './agentHelper';
import {
  createNewKnowledge,
  getOrCreateKnowledgeId,
  saveWebscrapConfig,
  uploadKnowledgeFiles,
} from './knowledgeHelper';

export interface UpdateAgentSubmitContext {
  methods: UseFormReturn<AgentFormData>;
  isBookingCapabilityEnabled: boolean;
  companyDetails?: Company;
  canEditAgentAccess: boolean;
  agent?: Agent;
  currentUser: User | null;
  knowledge?: Knowledge;
  knowledgeFilesData: KnowledgeFileData[];
  executeStagedDeletions: () => Promise<{ failed: string[] }>;
  clearKnowledgeFilesData: () => void;
  track: (event: string, props?: Record<string, unknown>) => void;
  displayMessage: SnackbarContextType;
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  setIsProcessing: (value: boolean) => void;
  setIsKnowledgeCreated: (value: boolean) => void;
  setIsAgentCreated: (value: boolean) => void;
  setIsFilesUploaded: (value: boolean) => void;
}

// Handles the EDIT-workflow branch of AgentForm's submit: restores any
// booking-capability fields missing from `data` (a session-storage OAuth
// recovery safety net — see useAgentFormSessionRecovery), updates the
// knowledge base (or switches to a different existing one), updates the
// agent, uploads new knowledge files and flushes staged deletions, and
// tracks analytics. See docs/specs/agent-form-click-to-jump-navigation.md.
export const submitUpdateAgent = async (
  data: AgentFormData,
  ctx: UpdateAgentSubmitContext
): Promise<void> => {
  const {
    methods,
    isBookingCapabilityEnabled,
    companyDetails,
    canEditAgentAccess,
    agent,
    currentUser,
    knowledge,
    knowledgeFilesData,
    executeStagedDeletions,
    clearKnowledgeFilesData,
    track,
    displayMessage,
    t,
    setIsProcessing,
    setIsKnowledgeCreated,
    setIsAgentCreated,
    setIsFilesUploaded,
  } = ctx;

  const getChangedFields = () =>
    Object.keys(methods.formState.dirtyFields || {});

  // Restore booking-capability fields from session storage before
  // submission (for consistency, only if booking capability is enabled).
  // Scoped to external_capabilities/transfer_calls/insert_appointment
  // ONLY — the sole fields EDIT's own Capabilities-step watcher
  // (saveCapabilitiesData in useAgentFormSessionRecovery) ever writes to
  // this key, which a full-page reload from the calendar OAuth redirect
  // can wipe from RHF's in-memory state before this submit. The behavior/
  // knowledge watchers that mirror agent_name, role_id, voice,
  // default_sentence, data_to_collect, default_language, knowledge_name,
  // and knowledge_language into this same key are CREATE-only, so EDIT
  // never legitimately produces those fields here — any value found for
  // them belongs to an unrelated CREATE (or earlier-edited-agent) session
  // left over in the same tab, and restoring them would silently
  // overwrite this agent's real, already-correct values with someone
  // else's leftovers.
  if (isBookingCapabilityEnabled) {
    const savedState = sessionStorage.getItem('elora_agent_form_state');
    if (savedState) {
      try {
        const parsedData = JSON.parse(savedState);
        // Ensure these fields are populated from session storage if not
        // in current form data
        if (!data.external_capabilities && parsedData.external_capabilities) {
          data.external_capabilities = parsedData.external_capabilities;
        }
        if (!data.transfer_calls && parsedData.transfer_calls) {
          data.transfer_calls = parsedData.transfer_calls;
        }
        if (!data.insert_appointment && parsedData.insert_appointment) {
          data.insert_appointment = parsedData.insert_appointment;
        }

        // Also update the form values in react-hook-form to ensure consistency
        if (parsedData.external_capabilities) {
          methods.setValue(
            'external_capabilities',
            parsedData.external_capabilities
          );
        }
        if (parsedData.transfer_calls) {
          methods.setValue('transfer_calls', parsedData.transfer_calls);
        }
        if (parsedData.insert_appointment) {
          methods.setValue('insert_appointment', parsedData.insert_appointment);
        }
      } catch (error) {
        console.warn(
          'Failed to restore form data from session storage:',
          error
        );
      }
    }
  }

  // Set only when this submit creates a knowledge base, so it can be rolled
  // back if the agent update then fails.
  let newKnowledgeBaseId: string | undefined;
  let webscrapWarning: string | undefined;

  //
  // Update the attached knowledge base, switch to a different existing one,
  // detach it, or create one for an agent that has none
  //
  try {
    let knowledgeIdsForAgent = data.knowledge_ids;
    // Where newly staged files get uploaded; none when detaching.
    let knowledgeIdForFiles = knowledge?.id;

    if (data.knowledge_mode === 'none') {
      // Detach only — the knowledge base itself is never deleted, since
      // other agents may still use it.
      knowledgeIdsForAgent = [];
      knowledgeIdForFiles = undefined;
      setIsKnowledgeCreated(true);
    } else if (data.existing_knowledge_id) {
      // User switched to a different existing knowledge base via the
      // toggle — detach and reference it instead of mutating the
      // previously attached one, which other agents may still use.
      knowledgeIdsForAgent = [data.existing_knowledge_id];
      setIsKnowledgeCreated(true);
    } else if (!knowledge) {
      // The agent has no knowledge base yet: create one and attach it.
      newKnowledgeBaseId = await getOrCreateKnowledgeId(undefined, () =>
        createNewKnowledge(
          data.knowledge_name,
          currentUser!.id,
          currentUser!.company_id,
          data.knowledge_language
        )
      );
      knowledgeIdsForAgent = [newKnowledgeBaseId];
      knowledgeIdForFiles = newKnowledgeBaseId;
      setIsKnowledgeCreated(true);
    } else {
      const knowledgeData: Knowledge = {
        name: data.knowledge_name,
        language: data.knowledge_language,
      };

      const updateKnowledgeResult = await updateKnowledge(
        data.knowledge_ids[0],
        knowledgeData
      );
      if (updateKnowledgeResult?.status === 'success') {
        setIsKnowledgeCreated(true);
      }

      // Saved separately so a rejected webscrap config (e.g. an invalid
      // exclude pattern) is reported as a warning instead of failing the
      // whole save, matching the other agent/knowledge forms.
      webscrapWarning = await saveWebscrapConfig(
        data.knowledge_ids[0],
        data,
        t
      );
    }

    //
    // Update an existing agent
    //
    const agentData: Agent = {
      avatar: !!methods.formState.dirtyFields.voice
        ? setAgentsImageFile()
        : data.avatar, // update only if voice field is updated as well
      company: companyDetails?.name ?? '',
      default_language: data.default_language,
      analytics_language: data.analytics_language,
      call_categories: data.call_categories,
      auto_fix: data.auto_fix,
      default_sentence: data.default_sentence,
      data_to_collect: data.data_to_collect,
      external_capabilities: {
        ...(data.transfer_calls === 'employee_directory'
          ? { transfer_calls: 'employee_directory' }
          : getTransferCallsArray(data.transfer_calls).length > 0
            ? {
                transfer_calls: getTransferCallsArray(data.transfer_calls).map(
                  (item: DepartmentContact) => ({
                    department_name: item.department_name,
                    phone_number: removeWhiteSpaces(item.phone_number),
                    ...(item.email ? { email: item.email } : {}),
                  })
                ),
              }
            : {}),
        ...(data.insert_appointment && data.insert_appointment.length > 0
          ? {
              insert_appointment: data.insert_appointment.map(
                (item: Appointment) => ({
                  start_time: item.start_time,
                  end_time: item.end_time,
                  min_advance_notice_hours: item.min_advance_notice_hours,
                  appointment_length_minutes: item.appointment_length_minutes,
                  app: item.app,
                })
              ),
            }
          : {}),
      },
      knowledge_ids: knowledgeIdsForAgent,
      name: data.agent_name,
      opening_sentence: data.opening_sentence,
      role_id: data.role_id,
      voice: data.voice,
      status: data.status,
    };

    if (canEditAgentAccess) {
      agentData.access = toAccessPayload(data.access);
    }
    // Send these unconditionally (not gated on truthiness) so that a
    // user clearing a previously saved value in Edit mode actually
    // persists as cleared, rather than silently leaving the stale
    // server value in place. See the Agent.ts comment on why
    // working_hours/feedback_sampling_rate use `null` as the
    // explicit-clear sentinel instead of an omitted key.
    agentData.feedback_sampling_rate =
      data.feedback_sampling_rate !== undefined
        ? data.feedback_sampling_rate / 100
        : null;
    agentData.time_zone = data.time_zone;
    agentData.working_hours = data.working_hours ?? null;
    agentData.holidays = data.holidays ?? [];

    const updatedAgent = await updateAgent(agent!.id!, agentData);

    // See the matching comment in the CREATE branch above: re-sync
    // holidays from the server's response rather than trusting local
    // form state, since the backend silently prunes past one-off
    // dates on every save.
    if (updatedAgent?.message?.holidays !== undefined) {
      methods.setValue('holidays', updatedAgent.message.holidays);
    }

    // --- Analytics: Track agent line choice and assignment success ---
    track(AnalyticsEvents.AGENT_LINE_CHOSEN, {
      agentId: agent!.id!,
      lineType: data.custom_phone_number ? 'custom' : 'system',
      phoneNumber: data.custom_phone_number || data.system_phone_number,
      countryCode: data.system_phone_number_country_code,
    });
    if (updatedAgent?.status === 'success') {
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_SUCCESS, {
        agentId: agent!.id!,
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config success ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_SUCCESS, {
        agentId: agent!.id!,
        capabilities: Object.keys(agentData.external_capabilities || {}),
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });
      setIsAgentCreated(true);
      // Track completion with changed fields
      const changedFields = getChangedFields();
      // --- Analytics: Track capability modified ---
      if (
        changedFields.includes('external_capabilities') ||
        changedFields.includes('transfer_calls')
      ) {
        track(AnalyticsEvents.AGENT_CAPABILITY_MODIFIED, {
          agentId: agent!.id!,
          changedFields,
          capabilities: Object.keys(agentData.external_capabilities || {}),
          transferCallCount: getTransferCallsArray(data.transfer_calls).length,
          departments: getTransferCallsArray(data.transfer_calls).map(
            (tc: DepartmentContact) => tc.department_name
          ),
        });
      }
      track(AnalyticsEvents.AGENT_EDIT_COMPLETED, {
        changedFields,
      });
    } else {
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_FAILURE, {
        error: updatedAgent?.error,
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config failure ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_FAILURE, {
        error: updatedAgent?.error,
        capabilities: Object.keys(agentData.external_capabilities || {}),
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });
    }

    // Settled (not `all`) so a failed knowledge-file upload can't
    // suppress the report of deletions that already completed.
    const [uploadResult, deletionResult] = await Promise.allSettled([
      knowledgeIdForFiles
        ? uploadKnowledgeFiles(knowledgeFilesData, {
            id: knowledgeIdForFiles,
          } as Knowledge)
        : Promise.resolve({ failed: [] as string[] }),
      executeStagedDeletions(),
    ]);

    setIsFilesUploaded(true);

    // Clear knowledge files data after successful upload
    clearKnowledgeFilesData();

    // The snackbar only holds one message at a time, so upload/deletion
    // problems (more actionable) take priority over the generic
    // success confirmation, and are combined into one message rather
    // than clobbering each other. Each part is a complete,
    // independently-translated sentence, joined with a space so
    // translated output stays grammatically valid in every locale.
    const parts: string[] = webscrapWarning ? [webscrapWarning] : [];
    if (
      uploadResult.status === 'fulfilled' &&
      uploadResult.value.failed.length > 0
    ) {
      parts.push(
        t('Could not upload: {{files}}', {
          files: uploadResult.value.failed.join(', '),
        })
      );
    }
    if (deletionResult.status === 'fulfilled') {
      if (deletionResult.value.failed.length > 0) {
        parts.push(
          t('Could not delete: {{files}}', {
            files: deletionResult.value.failed.join(', '),
          })
        );
      }
    } else {
      parts.push(
        deletionResult.reason instanceof Error
          ? deletionResult.reason.message
          : t('Some staged file deletions could not be completed.')
      );
    }

    if (parts.length > 0) {
      displayMessage(
        [
          t("Agent's behaviour and knowledge base were updated."),
          ...parts,
        ].join(' '),
        'warning',
        6000
      );
    } else {
      displayMessage(
        t("Agent's behaviour and knowledge base were updated."),
        'success'
      );
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      // Track failed save attempt
      const changedFields = getChangedFields();
      track(AnalyticsEvents.AGENT_EDIT_FAILED, {
        error: err.message,
        changedFields,
      });
      // --- Analytics: Track assignment failure ---
      track(AnalyticsEvents.AGENT_LINE_ASSIGNMENT_FAILURE, {
        error: err.message,
        lineType: data.custom_phone_number ? 'custom' : 'system',
        phoneNumber: data.custom_phone_number || data.system_phone_number,
        countryCode: data.system_phone_number_country_code,
      });
      // --- Analytics: Track capability config failure ---
      track(AnalyticsEvents.AGENT_CAPABILITY_CONFIG_FAILURE, {
        error: err.message,
        capabilities:
          getTransferCallsArray(data.transfer_calls).length > 0
            ? ['transfer_calls']
            : [],
        transferCallCount: getTransferCallsArray(data.transfer_calls).length,
        departments: getTransferCallsArray(data.transfer_calls).map(
          (tc: DepartmentContact) => tc.department_name
        ),
      });
      console.error(err.message);
      displayMessage(err.message, 'error', 8000);
      setIsProcessing(false);

      // Don't leave behind a knowledge base created for an agent update that
      // didn't go through.
      if (newKnowledgeBaseId) {
        await deleteKnowledge(newKnowledgeBaseId);
      }
    }
  }
};
